import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import LoadingIcon from './LoadingIcon'
import { PolicyDocumentsList } from './PolicyDocumentsCard'
import PolicyTabNav from './PolicyTabNav'
import PolicyReferralBlocked from './PolicyReferralBlocked'
import { ChevronLeftIcon } from './ui'
import { useApi } from '../services/api'
import type { PolicyLink } from '../services/types'
import { getHeaderVehicleLine } from '../utils/policyDisplay'
import {
  getPolicyReferralMessageFromApiError,
  isPolicyPortalBlockedApiError,
  isReferralBlockedPolicySummary,
} from '../utils/policyReferralState'
import { readPolicyFromListCache } from '../utils/policiesListCache'
import {
  buildPolicyDocumentsPageList,
  downloadPolicyDocument,
  type PolicyDocumentLinks,
} from '../utils/policyDocuments'

export default function PolicyDocumentsView() {
  const navigate = useNavigate()
  const location = useLocation()
  const { policyId } = useParams()
  const { getPolicy, getPolicies, fetchDocument } = useApi()
  const [loading, setLoading] = useState(true)
  const [documentLinks, setDocumentLinks] = useState<PolicyDocumentLinks>({})
  const [policyForClaimsNav, setPolicyForClaimsNav] = useState<PolicyLink | null>(null)
  const [referralMessage, setReferralMessage] = useState<string | null>(null)
  const [policyData, setPolicyData] = useState<{
    regoNumber: string
    headerVehicleLine: string
    has4X4Vehicle?: boolean
    non4x4InsuredAssetLabel?: string
    renewalDate: string
    policyNumber: string
  } | null>(null)

  useEffect(() => {
    if (!location.state?.preserveScroll) {
      window.scrollTo(0, 0)
    }
  }, [location.state?.preserveScroll])

  useEffect(() => {
    let cancelled = false

    const fetchPolicyData = async () => {
      if (!policyId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setReferralMessage(null)
        const decodedPolicyRef = decodeURIComponent(policyId)

        let policy: PolicyLink
        try {
          policy = await getPolicy(decodedPolicyRef)
        } catch (error: unknown) {
          if (isPolicyPortalBlockedApiError(error)) {
            if (!cancelled) setReferralMessage(getPolicyReferralMessageFromApiError(error))
            return
          }
          const err = error as { status?: number; message?: string }
          if (err?.status === 404 || err?.message?.includes('404') || err?.message?.includes('HTML')) {
            const response = await getPolicies()
            const foundPolicy = response.policies.find((p) => p.policyRef === decodedPolicyRef)
            if (!foundPolicy) throw new Error('Policy not found')
            if (isReferralBlockedPolicySummary(foundPolicy)) {
              if (!cancelled) setReferralMessage(foundPolicy.referralMessage ?? null)
              return
            }
            policy = foundPolicy
          } else {
            throw error
          }
        }

        if (cancelled) return

        if (isReferralBlockedPolicySummary(policy)) {
          if (!cancelled) {
            setReferralMessage(policy.referralMessage ?? null)
            setPolicyForClaimsNav(null)
          }
          return
        }

        const policyNumber = policy.policyNumber || policy.policyRef
        const regoNumber = policy.registrationNumber || policyNumber
        const headerVehicleLine = getHeaderVehicleLine(policy)

        let renewalDate: string
        if (policy.renewalDateFormatted) {
          renewalDate = policy.renewalDateFormatted
        } else if (policy.renewalDate) {
          renewalDate = new Date(policy.renewalDate).toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        } else {
          const linkedDate = new Date(policy.linkedAt)
          linkedDate.setFullYear(linkedDate.getFullYear() + 1)
          renewalDate = linkedDate.toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        }

        if (!cancelled) {
          setPolicyData({
            regoNumber,
            headerVehicleLine,
            has4X4Vehicle: policy.has4X4Vehicle,
            non4x4InsuredAssetLabel: policy.non4x4InsuredAssetLabel,
            renewalDate,
            policyNumber,
          })
          setDocumentLinks(policy.documentLinks ?? {})
          setPolicyForClaimsNav(policy)
          setLoading(false)
        }
      } catch (error) {
        if (isPolicyPortalBlockedApiError(error)) {
          if (!cancelled) setReferralMessage(getPolicyReferralMessageFromApiError(error))
          return
        }
        console.error('Error fetching policy data:', error)
        if (!cancelled) {
          navigate('/dashboard', { replace: true })
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void fetchPolicyData()
    return () => {
      cancelled = true
    }
  }, [policyId, getPolicy, getPolicies, navigate])

  const policyDocuments = useMemo(
    () => buildPolicyDocumentsPageList(policyId, documentLinks),
    [policyId, documentLinks]
  )

  const handleDocumentDownload = async (documentId: string) => {
    const doc = policyDocuments.find((d) => d.id === documentId)
    if (!doc?.downloadUrl) return

    try {
      await downloadPolicyDocument(doc, fetchDocument)
    } catch (e) {
      console.error('Document download failed:', e)
      alert('Download failed. Please try again or contact support.')
    }
  }

  if (referralMessage != null) {
    return (
      <PolicyReferralBlocked
        message={referralMessage}
        onBackToDashboard={() => {
          sessionStorage.removeItem('club4x4_policies_cache')
          navigate('/dashboard')
        }}
      />
    )
  }

  if (loading || !policyData) {
    return (
      <div className="bg-background-tertiary flex flex-col min-h-screen w-full">
        <DashboardNavigation />
        <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] md:py-[48px] relative shrink-0 w-full">
          <div className="flex flex-col gap-0 items-center md:items-start max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem('club4x4_policies_cache')
                navigate('/dashboard')
              }}
              className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
            >
              <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
                <ChevronLeftIcon />
              </div>
              <p className="font-body font-semibold text-sm leading-sm text-text-primary-on-brand">
                Back to dashboard
              </p>
            </button>
          </div>
        </div>
        <main id="main" className="flex flex-col flex-1 min-h-0 w-full px-[16px] md:px-[32px] py-[40px] md:py-[96px] max-w-container-desktop mx-auto">
          <div className="flex flex-1 min-h-0 w-full items-center justify-center">
            <LoadingIcon size="medium" message="Loading documents..." />
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="bg-background-brand-primary flex flex-col items-start relative min-h-screen w-full">
      <DashboardNavigation />

      <main id="main" className="flex w-full flex-col items-start">
      <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] relative shrink-0 w-full">
        <div className="flex flex-col gap-0 items-center md:items-start max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
          <div className="flex flex-col gap-[24px] items-start max-w-[768px] relative shrink-0 w-full">
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem('club4x4_policies_cache')
                navigate('/dashboard')
              }}
              className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
            >
              <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
                <ChevronLeftIcon />
              </div>
              <p className="font-body font-semibold text-sm leading-sm text-text-primary-on-brand">
                Back to dashboard
              </p>
            </button>

            <div className="flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              <div className="content-start flex flex-wrap gap-[8px] items-center relative shrink-0 w-full">
                <div className="bg-background-primary border border-text-primary-900 border-solid flex items-center px-[8px] py-[4px] relative rounded-xs shrink-0">
                  <p className="font-body font-medium text-md leading-md text-text-primary-900 text-center">
                    {policyData.regoNumber}
                  </p>
                </div>
                {policyData.has4X4Vehicle === false && (
                  <span className="font-body font-medium text-sm leading-sm text-text-primary-on-brand bg-white/20 px-[8px] py-[4px] rounded-xs">
                    {policyData.non4x4InsuredAssetLabel?.trim() || 'Non-4x4 policy'}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                <div className="flex gap-[8px] items-center relative shrink-0 w-full">
                  <p className="flex-1 font-display font-regular text-display-sm leading-[38px] text-text-primary-on-brand min-w-0 relative shrink-0">
                    {policyData.headerVehicleLine}
                  </p>
                </div>
                <div className="content-start flex flex-wrap gap-[6px] items-start leading-sm md:leading-md relative shrink-0 text-text-primary-on-brand text-sm md:text-md w-full">
                  <p className="font-body font-regular relative shrink-0">Renews on </p>
                  <p className="font-body font-medium relative shrink-0">{policyData.renewalDate}</p>
                </div>
                <div className="content-start flex flex-wrap gap-[6px] items-start leading-sm md:leading-md relative shrink-0 text-text-primary-on-brand text-sm md:text-md w-full">
                  <p className="font-body font-regular relative shrink-0">Policy Number </p>
                  <p className="font-body font-semibold relative shrink-0">{policyData.policyNumber}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-[32px] items-start max-w-container-desktop px-[16px] md:px-[32px] py-[32px] md:py-[48px] relative shrink-0 w-full mx-auto">
        <PolicyTabNav
          policyId={policyId}
          activeTab="documents"
          policyForClaimsNav={policyForClaimsNav ?? readPolicyFromListCache(decodeURIComponent(policyId || ''))}
        />

        <div className="flex flex-1 flex-col gap-[32px] items-center max-w-[768px] min-w-0 relative shrink-0 w-full">
          <PolicyDocumentsList documents={policyDocuments} onDownload={handleDocumentDownload} />
        </div>
      </div>
      </main>

      <Footer />
    </div>
  )
}
